import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { getLawyerById, updateLawyer, NewLawyerData } from '../../../services/lawyerService';
import formStyles from '../trainees/TraineeForm.module.css'; // إعادة استخدام نفس ملف التنسيق

// ملاحظة: في التعديل، قد لا تحتاج إلى كل حقول NewLawyerData
// لكن سنستخدمها لسهولة التوافق مع النموذج
type FormInputs = NewLawyerData;

const EditLawyerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const lawyerId = Number(id);

  const { 
    register, 
    handleSubmit, 
    reset, 
    formState: { errors, isSubmitting } 
  } = useForm<FormInputs>();

  useEffect(() => {
     console.log("ID from URL params:", id);
     const lawyerId = Number(id);
    console.log("ID after converting to Number:", lawyerId);
     if (!lawyerId) {
      console.error("Invalid or missing Lawyer ID. Navigating back.");
      toast.error("معرف المحامي غير صالح.");
      navigate('/admin/lawyers/list');
      return; // (3) الخروج المبكر إذا كان الـ ID غير صالح
    }

    const fetchLawyerData = async () => {
      try {
         console.log(`Fetching data for lawyer ID: ${lawyerId}`);
        const lawyerData = await getLawyerById(lawyerId);
        
        // تنسيق البيانات لتناسب حقول النموذج
        const formattedData = {
          ...lawyerData,
          join_date: format(new Date(lawyerData.join_date), 'yyyy-MM-dd'),
          expiry_date: format(new Date(lawyerData.expiry_date), 'yyyy-MM-dd'),
        };
        reset(formattedData); // ملء النموذج بالبيانات
      } catch (error: any) {
        console.error("Failed to fetch lawyer data:", error);
        toast.error(`فشل في جلب البيانات: ${error.response?.data?.message || error.message}`);
        navigate('/admin/lawyers/list');
      }
    };

    fetchLawyerData();
  }, [id, navigate, reset]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    const promise = updateLawyer(lawyerId, data);

    toast.promise(promise, {
      loading: 'جاري تحديث البيانات...',
      success: () => {
        navigate('/admin/lawyers/list');
        return 'تم تحديث بيانات المحامي بنجاح!';
      },
      error: (err) => err.response?.data?.message || 'فشل في تحديث البيانات.',
    });
  };

  return (
    <div className={formStyles.pageContainer}>
      <div className={formStyles.formContainer}>
        <h2 className={formStyles.title}>تعديل بيانات المحامي</h2>
        <form onSubmit={handleSubmit(onSubmit)} className={formStyles.form}>
          
          <div className={formStyles.formField}>
            <label htmlFor="name" className={formStyles.label}>اسم المحامي</label>
            <input id="name" {...register('name', { required: 'الاسم مطلوب' })} className={formStyles.input} />
            {errors.name && <p className={formStyles.error}>{errors.name.message}</p>}
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="bar_number" className={formStyles.label}>رقم القيد بالنقابة</label>
            <input id="bar_number" {...register('bar_number', { required: 'رقم القيد مطلوب' })} className={formStyles.input} />
            {errors.bar_number && <p className={formStyles.error}>{errors.bar_number.message}</p>}
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="specialization" className={formStyles.label}>التخصص</label>
            <input id="specialization" {...register('specialization', { required: 'التخصص مطلوب' })} className={formStyles.input} />
            {errors.specialization && <p className={formStyles.error}>{errors.specialization.message}</p>}
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="city" className={formStyles.label}>المدينة</label>
            <input id="city" {...register('city', { required: 'المدينة مطلوبة' })} className={formStyles.input} />
            {errors.city && <p className={formStyles.error}>{errors.city.message}</p>}
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="email" className={formStyles.label}>البريد الإلكتروني</label>
            <input id="email" type="email" {...register('email', { required: 'البريد الإلكتروني مطلوب' })} className={formStyles.input} />
            {errors.email && <p className={formStyles.error}>{errors.email.message}</p>}
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="phone" className={formStyles.label}>رقم الهاتف</label>
            <input id="phone" type="tel" {...register('phone', { required: 'رقم الهاتف مطلوب' })} className={formStyles.input} />
            {errors.phone && <p className={formStyles.error}>{errors.phone.message}</p>}
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="user_id" className={formStyles.label}>معرف المستخدم (User ID)</label>
            <input id="user_id" type="number" {...register('user_id', { required: 'معرف المستخدم مطلوب', valueAsNumber: true })} className={formStyles.input} readOnly />
            {errors.user_id && <p className={formStyles.error}>{errors.user_id.message}</p>}
          </div>

          <div className={formStyles.formField}>
            <label htmlFor="join_date" className={formStyles.label}>تاريخ الانضمام</label>
            <input id="join_date" type="date" {...register('join_date', { required: 'تاريخ الانضمام مطلوب' })} className={formStyles.input} readOnly />
            {errors.join_date && <p className={formStyles.error}>{errors.join_date.message}</p>}
          </div>

          <div className={`${formStyles.formField} ${formStyles.fullWidth}`}>
            <label htmlFor="expiry_date" className={formStyles.label}>تاريخ انتهاء الاشتراك</label>
            <input id="expiry_date" type="date" {...register('expiry_date', { required: 'تاريخ الانتهاء مطلوب' })} className={formStyles.input} />
            {errors.expiry_date && <p className={formStyles.error}>{errors.expiry_date.message}</p>}
          </div>

          <div className={formStyles.buttonContainer}>
            <Link to="/admin/lawyers/list" className={formStyles.cancelButton}>إلغاء</Link>
            <button type="submit" disabled={isSubmitting} className={formStyles.submitButton}>
              {isSubmitting ? 'جاري الحفظ...' : 'حفظ التعديلات'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditLawyerPage;
